import React, { useEffect } from 'react';
import { Button, Card, CardBody, CardTitle, Col, Container, Form, Input, Label, Row, FormFeedback } from "reactstrap";
import * as yup from "yup";
import { useFormik } from "formik";
import Breadcrumb from 'Components/UI/Breadcrumb';
import Select from "react-select";
import { useNavigate } from 'react-router-dom';
import { createUser } from 'helpers/API Calls/System & Settings/Users';

const USER_TYPE_OPTIONS = [
  { value: 'Admin', label: 'Admin' },
  { value: 'Manager', label: 'Manager' },
];

function AddUser() {
  const navigate = useNavigate();
  const formik = useFormik({    
    initialValues: {
      name: '',
      email: '',
      password: '',
      password_confirmation: '',
      user_type: '',
    },
    validationSchema: yup.object().shape({
      name: yup.string().required('Name is required'),
      email: yup.string().email('Invalid email').required('Email is required'),
      password: yup.string()
      .min(8, 'Password must be at least 8 characters')
        .matches(RegExp('(.*[a-z].*)'), 'Password must have at least one lowercase letter')
        .matches(RegExp('(.*[A-Z].*)'), 'Password must have at least one uppercase letter')
        .matches(RegExp('(.*[0-9].*)'), 'Password must have at least one number')
        .required("Password is required"),
      password_confirmation: yup.string()
        .oneOf([yup.ref('password')], 'Passwords must match')
        .required('Password confirmation is required'),
      user_type: yup.string().required('User type is required'),
    }),      
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const userData = {
        name: values.name,
        email: values.email,
        password: values.password,
        password_confirmation: values.password_confirmation,
        user_type: values.user_type,
      };
      const response = await createUser(userData);
      if (response.success) {
        navigate('/users');
      }
      setSubmitting(false);
    },
  });
  // Add scroll to error effect
  useEffect(() => {
    if (Object.keys(formik.errors).length > 0 && formik.isSubmitting) {
      const firstErrorKey = Object.keys(formik.errors)[0];
      const element = document.getElementById(firstErrorKey);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    }
  }, [formik.errors, formik.isSubmitting]);
  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb items={[{ label: "Users", link: "/users" }, {label: 'Create'}]} />
        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit} autoComplete="off">
              {/* Basic Information */}              
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>User Information</CardTitle>
                <Row>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="name">Name <span className="text-danger">*</span></Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Enter name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        invalid={formik.touched.name && formik.errors.name ? true : false}
                      />
                      {formik.errors.name && formik.touched.name && (
                        <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="email">Email <span className="text-danger">*</span></Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Enter email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        invalid={formik.touched.email && formik.errors.email ? true : false}
                      />
                      {formik.errors.email && formik.touched.email && (
                        <FormFeedback type="invalid">{formik.errors.email}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="password">Password <span className="text-danger">*</span></Label>
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        placeholder="Enter password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        invalid={formik.touched.password && formik.errors.password ? true : false}
                      />
                      {formik.errors.password && formik.touched.password && (
                        <FormFeedback type="invalid">{formik.errors.password}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="password_confirmation">Confirm Password <span className="text-danger">*</span></Label>
                      <Input
                        id="password_confirmation"
                        name="password_confirmation"
                        type="password"
                        placeholder="Confirm password"
                        value={formik.values.password_confirmation}
                        onChange={formik.handleChange}
                        invalid={formik.touched.password_confirmation && formik.errors.password_confirmation ? true : false}
                      />
                      {formik.errors.password_confirmation && formik.touched.password_confirmation && (
                        <FormFeedback type="invalid">{formik.errors.password_confirmation}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="user_type">User Type <span className="text-danger">*</span></Label>
                      <Select
                        id="user_type"
                        name="user_type"
                        placeholder="Select User Type"
                        options={USER_TYPE_OPTIONS}
                        value={USER_TYPE_OPTIONS.find(option => option.value === formik.values.user_type)}
                        onChange={(option) => formik.setFieldValue("user_type", option?.value)}
                        onBlur={formik.handleBlur}
                        className={
                          formik.touched.user_type && formik.errors.user_type
                            ? "is-invalid"
                            : ""
                        }
                      />
                      {formik.errors.user_type && formik.touched.user_type && (
                        <div className="invalid-feedback d-block">{formik.errors.user_type}</div>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="d-flex justify-content-end">
                <Button type="submit" color="primary" disabled={formik.isSubmitting}>
                  {formik.isSubmitting ? 'Submitting...' : 'Create User'}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}

export default AddUser;
